import { isTouchDevice } from './viewport';

/**
 * Layout enum. Five variants ported from legacy
 * `storyscale-widgets/src/agent-chat-v2/constants/layouts.js`. Names
 * preserved so legacy design assets + customer docs still map cleanly.
 *
 * - ICON      — minimal launcher pill in a bottom corner; expands to INPUT/FULL
 * - INPUT     — bottom-center pill with visible text input and a mini video
 *               greeter avatar; "skyscraper-adjacent" per the legacy design
 * - ENGAGE    — proactive start-view panel: big greeter video front and
 *               center, welcome message, suggestion chips, input pill.
 *               Compact corner card that appears on page load to signal
 *               the agent is here. Targeting can set this as the initial
 *               position to "show the agent from page load."
 * - EXPANDED  — the classic site-agent panel, bottom-center, clamp height
 * - SIDEBAR   — full-height right-edge rail, used for content-rich sessions;
 *               body gets right-padding so page content doesn't sit under it
 * - FULL      — centered 3-column modal (playlist | chat | content), backdrop;
 *               triggered when the user opens a playlist/content item
 *
 * Mobile (<768px) collapses EXPANDED/SIDEBAR/FULL to fullscreen — no modal
 * chrome. INPUT shrinks to 90vw. ICON stays a pill in the corner.
 */

export const LAYOUTS = {
  ICON: 'icon',
  INPUT: 'input',
  ENGAGE: 'engage',
  EXPANDED: 'expanded',
  SIDEBAR: 'sidebar',
  FULL: 'full',
} as const;

export type Layout = (typeof LAYOUTS)[keyof typeof LAYOUTS];

export const ALL_LAYOUTS: readonly Layout[] = [
  LAYOUTS.ICON,
  LAYOUTS.INPUT,
  LAYOUTS.ENGAGE,
  LAYOUTS.EXPANDED,
  LAYOUTS.SIDEBAR,
  LAYOUTS.FULL,
] as const;

export function isLayout(value: string | null | undefined): value is Layout {
  return !!value && (ALL_LAYOUTS as readonly string[]).includes(value);
}

/**
 * Which layout the widget boots into. Legacy default was INPUT, but INPUT
 * always shows the text field which is heavy for a first load — modern
 * chat widgets start collapsed. Default ICON unless explicitly overridden.
 */
export const DEFAULT_LAYOUT: Layout = LAYOUTS.ICON;

/**
 * Legacy rule: when resuming a session across page navigation, the widget
 * forces SIDEBAR so content stays visible. We preserve this — it's a UX
 * choice that customers gave positive feedback on.
 */
export const RESUME_LAYOUT: Layout = LAYOUTS.SIDEBAR;

/**
 * When the user clicks the ICON launcher, which layout expands to?
 * Legacy mapped this per agent configuration; for v1 we default to
 * EXPANDED (site-agent panel). Mobile overrides to FULL via media query.
 */
export const EXPAND_FROM_ICON: Layout = LAYOUTS.EXPANDED;

/**
 * Layouts that would visually take over a phone screen — the visitor
 * lands on the customer site and sees only chat. We cap initial load
 * on phones to a non-takeover layout (INPUT / Site Agent) unless the
 * agent has explicitly opted in via `mobileTakeoverAllowed`.
 */
export const MOBILE_TAKEOVER_LAYOUTS: readonly Layout[] = [
  LAYOUTS.EXPANDED,
  LAYOUTS.SIDEBAR,
  LAYOUTS.FULL,
] as const;

export function isMobileTakeoverLayout(layout: Layout): boolean {
  return (MOBILE_TAKEOVER_LAYOUTS as readonly string[]).includes(layout);
}

/**
 * Touch-device detection. We key off input modality, not viewport size —
 * `pointer: coarse` matches every touch device regardless of form factor
 * (phones, all iPads, Surface in tablet mode, touch Chromebooks, future
 * foldables). Width-based breakpoints chased form factors and lost; this
 * one rule survives whatever ships next.
 *
 * The takeover-layout cap uses this: on touch devices we downgrade
 * EXPANDED / SIDEBAR / FULL to INPUT at boot so the visitor lands on the
 * host page, not on a screen-takeover.
 *
 * A docked iPad with a trackpad reports `pointer: fine` and gets the
 * desktop UX — correct, since the user has precise input.
 *
 * Returns false in SSR / non-browser contexts.
 *
 * M1 (mobile UX program): now a thin wrapper over the viewport module's
 * `isTouchDevice()` — SAME media query, same semantics, one source of
 * truth. Phone-vs-tablet distinctions use `viewport.deviceSig`.
 */
export function isMobileViewport(): boolean {
  return isTouchDevice();
}

/**
 * Apply the mobile takeover cap. On phones, takeover layouts collapse
 * to INPUT (Site Agent). On larger screens or for non-takeover layouts
 * the input passes through unchanged.
 *
 * The opt-in flag (`mobileTakeoverAllowed`) lives on the agent config,
 * which we don't have synchronously at boot — so callers cap first and
 * restore later once the server response arrives.
 */
export function capLayoutForMobile(layout: Layout): Layout {
  return isMobileViewport() && isMobileTakeoverLayout(layout) ? LAYOUTS.INPUT : layout;
}
